/**
 * Moving a patient to another ward or bed — DECISIONS.md #48.
 *
 * Mirrors `api/src/Continuum.Api/Transfers/TransferEndpoints.cs`'s one route and the codes
 * `PatientTransfer.Validate` and `PatientTransferService` return.
 *
 * **A move is the next version of the patient's encounter.** The vårdtillfälle continues, so the
 * admission time stays what it was and the history of moves is the encounter's versions. The request
 * names the encounter in its path and the exact version it supersedes in its body — the shape every
 * correction here has — so a move made from a screen that is out of date is refused rather than
 * applied over a move somebody else has just made.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/** What the transfer form submits. Mirrors `PatientTransferRequest`. */
export interface TransferSubmission {
  /** The encounter `version_id` on screen. */
  readonly supersedes: string

  /** The destination ward's code. */
  readonly ward: string

  /** The destination bed, or null for none — a corridor is a real place to move a patient to. */
  readonly bed: string | null

  /**
   * When the patient moved, as an ISO instant in UTC. Required: the server never fills it in, because
   * a move documented after the fact is the normal case and "now" would record the documenting.
   */
  readonly movedAt: string
}

/** What a successful move returns. Mirrors `TransferredPatient`. */
export interface TransferredPatient {
  /** The encounter's `record_id`, unchanged — this is the same vårdtillfälle. */
  readonly encounterId: string

  /** The new version's `version_id`. What the *next* move must supersede. */
  readonly versionId: string

  /** 2 for the first move, then 3, 4 … */
  readonly version: number

  /** The `version_id` that was superseded. */
  readonly supersedes: string

  /** The ward the patient is now on. */
  readonly ward: string
}

/**
 * Every code a move can be refused with.
 *
 * Listed rather than left as `string`, so a code added on the server without wording here is a
 * typecheck failure rather than a blank message. `transferRefusalMessage` is exhaustive over it.
 */
export const TRANSFER_REFUSAL_CODES = [
  'TRANSFER_BODY_MISSING',
  'TRANSFER_SUPERSEDES_MISSING',
  'TRANSFER_WARD_MISSING',
  'TRANSFER_FIELD_TOO_LONG',
  'TRANSFER_MOVED_AT_MISSING',
  'TRANSFER_MOVED_AT_MALFORMED',
  'TRANSFER_MOVED_AT_IN_FUTURE',
  'TRANSFER_ENCOUNTER_NOT_FOUND',
  'TRANSFER_ENCOUNTER_NOT_ACTIVE',
  'TRANSFER_VERSION_STALE',
  'TRANSFER_WARD_NOT_FOUND',
  'TRANSFER_BEFORE_CURRENT',
  'TRANSFER_NO_CHANGE',
  'ROLE_MAY_NOT_DOCUMENT',
] as const

export type TransferRefusalCode = (typeof TRANSFER_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface TransferRefusalParams {
  /** `TRANSFER_FIELD_TOO_LONG`: which field and the limit. */
  readonly field?: string
  readonly max?: number

  /** `TRANSFER_WARD_NOT_FOUND`: the code that was sent. */
  readonly ward?: string

  /**
   * `TRANSFER_BEFORE_CURRENT`: since when the patient has been where they are. **Displayed** — it is
   * what makes the refusal actionable, since the form cannot see an inherited column.
   */
  readonly since?: string

  /** `TRANSFER_ENCOUNTER_NOT_ACTIVE`: the encounter's status. */
  readonly status?: string

  /** `TRANSFER_VERSION_STALE`: the version named, and the current one when known. Not displayed. */
  readonly supersedes?: string
  readonly current?: string

  /** `TRANSFER_ENCOUNTER_NOT_FOUND`: the id that did not resolve. */
  readonly encounterId?: string

  /** `ROLE_MAY_NOT_DOCUMENT`: the role that was refused. */
  readonly roleCode?: string
}

/** The server refused the move, with the code it refused by. */
export class TransferRefusedError extends Error {
  readonly code: TransferRefusalCode | 'UNKNOWN'
  readonly params: TransferRefusalParams
  readonly status: number

  constructor(code: TransferRefusalCode | 'UNKNOWN', params: TransferRefusalParams, status: number) {
    super(`Transfer refused: ${code}`)
    this.name = 'TransferRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class TransferUnavailableError extends Error {
  constructor() {
    super('Transfer unavailable')
    this.name = 'TransferUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is TransferRefusalCode {
  return typeof value === 'string' && (TRANSFER_REFUSAL_CODES as readonly string[]).includes(value)
}

/**
 * Turns a non-2xx response into the error to throw. An unreadable body is still a refusal with code
 * `UNKNOWN`: the server answered, so nothing was written.
 */
async function refusalFrom(response: Response): Promise<TransferRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: TransferRefusalParams }
    }

    return new TransferRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new TransferRefusedError('UNKNOWN', {}, response.status)
  }
}

/**
 * Moves a patient: one new `clinical.encounter` version and its audit row, in one transaction on the
 * server.
 *
 * **What this writes cannot be removed** (AGENTS.md §3). The placement the patient is leaving stays in
 * the record as the previous version, which is what makes "where was this patient on Tuesday"
 * answerable — and a move to the wrong ward is undone by moving them again, not by deleting the move.
 */
export async function transferPatient(
  patientId: string,
  encounterId: string,
  submission: TransferSubmission,
  signal: AbortSignal,
): Promise<TransferredPatient> {
  const url =
    `/api/patients/${encodeURIComponent(patientId)}` +
    `/encounters/${encodeURIComponent(encounterId)}/versions`

  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request, not a failure.
    if (signal.aborted) {
      throw cause
    }
    throw new TransferUnavailableError()
  }

  // Before `refusalFrom`: a 401 is the session having ended, not a refusal of this move.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as TransferredPatient
  } catch {
    // A 201 whose body did not parse: the move is in the record and this client cannot say which
    // version it became. Unavailable, so the clinician reopens the patient and reads where they are
    // instead of moving them a second time.
    throw new TransferUnavailableError()
  }
}
